import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useI18n } from "../I18nContext";

function methodLine(method, lang) {
  if (!method) return "";
  const title = lang === "fr" ? (method.title_fr || method.title_en) : (method.title_en || method.title_fr);
  const stage = lang === "fr" ? (method.stage_name_fr || method.stage_name_en) : (method.stage_name_en || method.stage_name_fr);
  return stage ? `${title} — ${stage}` : title;
}

function snapRows(snaps) {
  return snaps.flatMap((snap) => {
    const methods = snap.methodologies || [];
    if (methods.length <= 1) return [{ snap, index: 0, label: methods[0] }];
    return methods.map((label, index) => ({ snap, index, label }));
  });
}

export default function SnapshotHistory() {
  const { kind } = useParams();
  const [params] = useSearchParams();
  const userId = params.get("user_id");
  const { t, lang } = useI18n();
  const [snaps, setSnaps] = useState([]);
  const [active, setActive] = useState(null);
  const [activeMethod, setActiveMethod] = useState(0);
  const [err, setErr] = useState("");

  useEffect(() => {
    const q = userId ? `?user_id=${userId}` : "";
    api.get(`/snapshots/${kind}${q}`)
      .then((r) => setSnaps(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [kind, userId]);

  const backTo = userId ? `/app/u/${userId}?tab=${kind}` : `/app/portfolio#${kind}`;

  const open = (sid, index) => {
    api.get(`/snapshots/${kind}/${sid}`).then((r) => {
      const data = r.data;
      if (data?.kind === "framework" && (data.payload?.methodologies || []).length > 1) {
        const method = data.payload.methodologies[index];
        data.payload = { ...data.payload, methodologies: method ? [method] : [] };
      }
      setActiveMethod(index);
      setActive(data);
    }).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };

  return (
    <div className="tb-page">
      <div style={{ marginBottom: "1rem" }}>
        <Link to={backTo} className="tb-back-link" data-testid="history-back">← {t("common.back")}</Link>
      </div>
      <h1 className="tb-h1" data-testid="history-heading">{t(`snapshot.history_${kind}`)}</h1>
      <p className="tb-muted" style={{ marginBottom: "1.5rem" }}>{t("snapshot.historySubtitle")}</p>
      {err && <div className="tb-form-error" style={{ marginBottom: 16 }}>{err}</div>}

      {snaps.length === 0 ? (
        <p className="tb-muted" data-testid="history-empty">{t("snapshot.noneYet")}</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 280px) 1fr", gap: "1.5rem" }}>
          <div className="tb-block" style={{ padding: 8 }}>
            {snapRows(snaps).map((row) => (
              <button
                key={`${row.snap.id}-${row.index}`}
                onClick={() => open(row.snap.id, row.index)}
                data-testid={`snap-${row.snap.id}-${row.index}`}
                className={active?.id === row.snap.id && activeMethod === row.index ? "tb-snap-row active" : "tb-snap-row"}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  padding: "10px 12px", marginBottom: 4, borderRadius: 8,
                  border: "1px solid var(--border)",
                  background: active?.id === row.snap.id && activeMethod === row.index ? "var(--accent-dim)" : "var(--bg-elevated)",
                  cursor: "pointer", color: "var(--text)",
                }}
              >
                <div style={{ fontWeight: 600, fontSize: "0.86rem" }}>
                  {[row.snap.created_by_name, new Date(row.snap.created_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-US", { dateStyle: "medium", timeStyle: "short" })].filter(Boolean).join(" · ")}
                </div>
                {row.label && (
                  <div className="tb-muted" style={{ fontSize: "0.78rem", marginTop: 4 }} data-testid={`snap-methods-${row.snap.id}-${row.index}`}>
                    {methodLine(row.label, lang)}
                  </div>
                )}
              </button>
            ))}
          </div>
          <div className="tb-block">
            {!active ? (
              <p className="tb-muted">{t("snapshot.pickOne")}</p>
            ) : (
              <SnapshotRenderer snap={active} t={t} lang={lang} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function stepStatusLabel(status, t) {
  if (status === "validated") return t("mit.validated");
  if (status === "todo") return t("mit.todo");
  if (status === "in_progress") return t("mit.in_progress");
  return status || "—";
}

function MethodologySnapshot({ method, t, lang, first }) {
  const title = lang === "fr" ? (method.title_fr || method.title_en) : (method.title_en || method.title_fr);
  const stage = lang === "fr"
    ? (method.stage_name_fr || method.stage_name_en || method.stage)
    : (method.stage_name_en || method.stage_name_fr || method.stage);
  const readiness = method.readiness || [];
  const steps = method.steps || [];
  return (
    <section style={{ marginTop: first ? 0 : 28 }} data-testid={`history-method-${method.slug || method.id || "item"}`}>
      <h3 style={{ marginTop: 0, fontFamily: "var(--font-display)" }}>{title || t("framework.title")}</h3>
      {stage ? <p><strong>{t("framework.yourStage")}:</strong> {stage}</p> : null}
      {readiness.map((row) => (
        <div key={row.key} style={{ marginBottom: 8 }}>
          <span className="tb-muted" style={{ fontSize: "0.78rem" }}>{lang === "fr" ? row.name_fr || row.name_en || row.key : row.name_en || row.name_fr || row.key}</span>
          <div style={{ fontWeight: 500 }}>{row.level}</div>
        </div>
      ))}
      {steps.length > 0 && (
        <div>
          {steps.map((step) => (
            <div key={step.step_id} style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 6 }}>
              <span>{lang === "fr" ? step.name_fr || step.name_en : step.name_en || step.name_fr}</span>
              <span className="tb-muted">{stepStatusLabel(step.status, t)}</span>
            </div>
          ))}
        </div>
      )}
      {!stage && readiness.length === 0 && steps.length === 0 ? <p className="tb-muted">—</p> : null}
    </section>
  );
}

function SnapshotRenderer({ snap, t, lang }) {
  if (snap.kind === "metrics") {
    const items = snap.payload?.metrics || [];
    return (
      <div>
        <h3 style={{ marginTop: 0, fontFamily: "var(--font-display)" }}>{t("metrics.title")}</h3>
        {items.length === 0 ? <p className="tb-muted">—</p> : (
          <div className="tb-metric-grid">
            {items.map((m) => (
              <div className="tb-metric-card" key={m.id || m.key}>
                <div className="tb-metric-label"><span>{(lang === "fr" ? m.label_fr || m.label_en : m.label_en) || m.key}</span></div>
                <div className="tb-metric-value"><span>{m.value || "—"}</span>{m.unit && <small className="tb-muted">{m.unit}</small>}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
  if (snap.kind === "roadmap") {
    const r = snap.payload?.roadmap || {};
    const rows = [
      ["northStar", r.north_star], ["year10", r.year_10], ["year5", r.year_5],
      ["year2", r.year_2], ["year1", r.year_1], ["month1", r.month_1], ["week2", r.week_2],
    ];
    return (
      <div>
        <h3 style={{ marginTop: 0, fontFamily: "var(--font-display)" }}>{t("roadmap.title")}</h3>
        {rows.map(([k, v]) => (
          <div key={k} style={{ marginBottom: 12 }}>
            <div className="tb-muted" style={{ fontSize: "0.78rem" }}>{t(`roadmap.${k}`)}</div>
            <div style={{ fontWeight: 500 }}>{v || "—"}</div>
          </div>
        ))}
        {(r.quarters || []).some(Boolean) && (
          <div>
            <div className="tb-muted" style={{ fontSize: "0.78rem", marginBottom: 4 }}>{t("roadmap.quarters")}</div>
            <ol>
              {(r.quarters || []).map((q, i) => <li key={i}>{q || "—"}</li>)}
            </ol>
          </div>
        )}
      </div>
    );
  }
  if (snap.kind === "framework") {
    const p = snap.payload || {};
    const methods = (p.methodologies && p.methodologies.length) ? p.methodologies : [{
      title_en: p.framework_title_en,
      title_fr: p.framework_title_fr,
      stage: p.framework_stage,
      stage_name_en: p.framework_stage_name_en,
      stage_name_fr: p.framework_stage_name_fr,
    }];
    return (
      <div data-testid="history-methodologies">
        {methods.map((m, index) => (
          <MethodologySnapshot key={m.id || m.slug || index} method={m} t={t} lang={lang} first={index === 0} />
        ))}
      </div>
    );
  }
  return <pre>{JSON.stringify(snap, null, 2)}</pre>;
}
